import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";

import "@/App.css";
import "@/index.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";

import Home from "@/pages/Home";
import ToolsPage from "@/pages/Tools";
import ToolDetail from "@/pages/ToolDetail";
import Pricing from "@/pages/Pricing";
import { BuyCredits, Checkout } from "@/pages/BuyCredits";
import { BlogIndex, BlogPost } from "@/pages/Blog";
import { Login, Register } from "@/pages/Auth";
import { LegalPage, FAQPage, ContactPage } from "@/pages/StaticPages";
import UserDashboardHome from "@/pages/UserDashboard";
import { UserToolsList, UserHistory, UserCreditHistory, UserInvoices, UserSupport, UserSettings } from "@/pages/UserPages";
import {
  AdminOverview, AdminUsers, AdminTools, AdminPlans, AdminGateways,
  AdminInvoices as AdminGatewayInvoices, AdminTransactions
} from "@/pages/AdminCore";
import { AdminBlog, AdminPages, AdminFAQ, AdminTickets, AdminMessages } from "@/pages/AdminContent";
import { AdminSMTP, AdminBulkBlog } from "@/pages/AdminExtras";
import Affiliate from "@/pages/Affiliate";
import AdminAffiliate from "@/pages/AdminAffiliate";
import AdminSettings from "@/pages/AdminSettings";
import AdminCategories from "@/pages/AdminCategories";
import AdminAuditLog from "@/pages/AdminAuditLog";
import AdminUPI from "@/pages/AdminUPI";
import UPIPay from "@/pages/UPIPay";
import PayLink from "@/pages/PayLink";
import UPIThanks from "@/pages/UPIThanks";
import AdminInvoices from "@/pages/AdminInvoices";
import AdminInvoiceEdit from "@/pages/AdminInvoiceEdit";
import InvoiceSign from "@/pages/InvoiceSign";
import InvoicePublicView from "@/pages/InvoicePublicView";
import SetPasswordPage from "@/pages/SetPassword";
import AdminAggregators from "@/pages/AdminAggregators";
import AdminEmailTemplates from "@/pages/AdminEmailTemplates";
import AdminSEO from "@/pages/AdminSEO";

function RequireAuth({ children, admin = false }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (admin && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

export default function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster theme="dark" position="top-right" richColors />
        <Routes>
          {/* Marketing */}
          <Route path="/" element={<Home />} />
          <Route path="/tools" element={<ToolsPage />} />
          <Route path="/tools/:slug" element={<ToolDetail />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/buy-credits" element={<BuyCredits />} />
          <Route path="/buy-credits/checkout/:orderId" element={<Checkout />} />
          <Route path="/blog" element={<BlogIndex />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/about" element={<LegalPage slug="about" />} />
          <Route path="/privacy" element={<LegalPage slug="privacy" />} />
          <Route path="/terms" element={<LegalPage slug="terms" />} />
          <Route path="/refund" element={<LegalPage slug="refund" />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/contact" element={<ContactPage />} />

          {/* Auth */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/set-password" element={<SetPasswordPage />} />

          {/* User Dashboard */}
          <Route path="/dashboard" element={<RequireAuth><UserDashboardHome /></RequireAuth>} />
          <Route path="/dashboard/tools" element={<RequireAuth><UserToolsList /></RequireAuth>} />
          <Route path="/dashboard/history" element={<RequireAuth><UserHistory /></RequireAuth>} />
          <Route path="/dashboard/credits" element={<RequireAuth><UserCreditHistory /></RequireAuth>} />
          <Route path="/dashboard/invoices" element={<RequireAuth><UserInvoices /></RequireAuth>} />
          <Route path="/dashboard/support" element={<RequireAuth><UserSupport /></RequireAuth>} />
          <Route path="/dashboard/affiliate" element={<RequireAuth><Affiliate /></RequireAuth>} />
          <Route path="/dashboard/settings" element={<RequireAuth><UserSettings /></RequireAuth>} />

          {/* Admin */}
          <Route path="/admin" element={<RequireAuth admin><AdminOverview /></RequireAuth>} />
          <Route path="/admin/users" element={<RequireAuth admin><AdminUsers /></RequireAuth>} />
          <Route path="/admin/tools" element={<RequireAuth admin><AdminTools /></RequireAuth>} />
          <Route path="/admin/categories" element={<RequireAuth admin><AdminCategories /></RequireAuth>} />
          <Route path="/admin/audit-log" element={<RequireAuth admin><AdminAuditLog /></RequireAuth>} />
          <Route path="/admin/upi" element={<RequireAuth admin><AdminUPI /></RequireAuth>} />
          <Route path="/admin/invoices" element={<RequireAuth admin><AdminInvoices /></RequireAuth>} />
          <Route path="/admin/invoices/:id" element={<RequireAuth admin><AdminInvoiceEdit /></RequireAuth>} />

          {/* Public mobile-only UPI payment page (case-insensitive) */}
          <Route path="/UPI" element={<UPIPay />} />
          <Route path="/upi" element={<UPIPay />} />
          {/* Per-user pre-filled UPI checkout link (24h, single-use) */}
          <Route path="/pay/:token" element={<PayLink />} />
          {/* Post-payment confirmation page (polls /upi/payment-status/:ref_id) */}
          <Route path="/upi/thanks/:refId" element={<UPIThanks />} />
          {/* Public invoice signature + view pages */}
          <Route path="/invoice/sign/:token" element={<InvoiceSign />} />
          <Route path="/invoice/view/:token" element={<InvoicePublicView />} />
          <Route path="/admin/plans" element={<RequireAuth admin><AdminPlans /></RequireAuth>} />
          <Route path="/admin/gateways" element={<RequireAuth admin><AdminGateways /></RequireAuth>} />
          <Route path="/admin/invoices/gateway" element={<RequireAuth admin><AdminGatewayInvoices /></RequireAuth>} />
          <Route path="/admin/transactions" element={<RequireAuth admin><AdminTransactions /></RequireAuth>} />
          <Route path="/admin/affiliate" element={<RequireAuth admin><AdminAffiliate /></RequireAuth>} />
          <Route path="/admin/blog" element={<RequireAuth admin><AdminBlog /></RequireAuth>} />
          <Route path="/admin/blog/bulk" element={<RequireAuth admin><AdminBulkBlog /></RequireAuth>} />
          <Route path="/admin/pages" element={<RequireAuth admin><AdminPages /></RequireAuth>} />
          <Route path="/admin/faq" element={<RequireAuth admin><AdminFAQ /></RequireAuth>} />
          <Route path="/admin/smtp" element={<RequireAuth admin><AdminSMTP /></RequireAuth>} />
          <Route path="/admin/settings" element={<RequireAuth admin><AdminSettings /></RequireAuth>} />
          <Route path="/admin/tickets" element={<RequireAuth admin><AdminTickets /></RequireAuth>} />
          <Route path="/admin/messages" element={<RequireAuth admin><AdminMessages /></RequireAuth>} />
          <Route path="/admin/aggregators" element={<RequireAuth admin><AdminAggregators /></RequireAuth>} />
          <Route path="/admin/email-templates" element={<RequireAuth admin><AdminEmailTemplates /></RequireAuth>} />
          <Route path="/admin/seo" element={<RequireAuth admin><AdminSEO /></RequireAuth>} />

          {/* 404 */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  );
}
